import React from 'react';
import styled, { css } from 'styled-components';
import { get } from 'lodash';
import moment from 'moment';
import {
  Heading2,
  FlexBox,
  BaseBox,
  Body1,
  Body2,
  Icons,
  ellipsis,
} from '@docon/docon-ui-library';
import { ClinicIcon, TimeIcon } from 'shared/icons';
import { getTimeFromMinutes } from 'utils';
import { borderColor, colors } from 'utilities/colorConfig';

export enum TELE_CONSULT_STATUS {
  REQUESTED = 'Requested',
  PAYMENT_INITIATED = 'Payment Initiated',
  PAYMENT_UNDER_PROCESS = 'Payment under process',
  PAYMENT_FAILED = 'Payment failed',
  PAYMENT_WAITING = 'Payment waiting',

  REFUND_INITIATED = 'Refund initiated',
  REFUND_WAITING_ON_PG = 'Refund waiting on pg',
  REFUND_FAILED = 'Refund failed',
  REFUND_BY_DOCTOR = 'Refund',

  EXPIRED = 'Expired',
  CONFIRMED = 'Paid',
  CANCELLED = 'Refund',
  PAID = 'Paid',
  CONFIRMATION_FAILED = 'Paid',
  IN_PROGRESS = 'Paid',
  CLOSED = 'Paid',
  NO_SHOW = 'Paid',
}

const Card = styled(BaseBox)`
  padding: 12px 26px;
  border-radius: 8px;
  background: ${colors.white};
  border: 1px solid ${borderColor};

  > div:last-child {
    padding-bottom: 0;
  }
`;

const Footer = styled(FlexBox)`
  padding-top: 7px;
  border-top: 1px solid ${borderColor};
`;

const DateBox = styled(FlexBox)`
  width: 70px;
  height: 70px;
  margin-right: 12px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;

  ${Heading2} {
    font-weight: 600;
    color: ${colors.white};
    margin-bottom: 0;
  }
`;

const InfoText = styled(Body1)`
  font-weight: 600;
  color: ${colors.secondaryColor};
  ${ellipsis};

  ${({ $fs }: any) => $fs && css`
    font-size: ${$fs}px;
  `}
`;

const DisplayTime = styled(InfoText)`
  font-size: ${({ $fs }) => `${$fs || 16}px`};
`;

const StatusBox = styled(Body2)`
  min-width: 86px;
  padding: 2px 12px;
  margin-left: auto;
  align-self: center;
  text-align: center;
  border-radius: 10px;
  color: ${colors.white};
`;

const AppointmentCard = ({
  className,
  hideFooter,
  hideDelay,
  type,
  appointment = {},
  onCardClick,
  ...props
}: any) => {

  const {
    patientDetails: {
      firstName: pFirstName = '',
      lastName: pLastName = '',
      officeID = '',
    } = {},
    doctordetails: {
      firstName: dFirstName = '',
      lastName: dLastName = '',
    } = {},
    userDetails: {
      firstName: uFirstName = '',
      lastName: uLastName = '',
    } = {},
    status,
    delay,
    appointmentTime,
    appointmentDisplayTime,
    teleConsultSessionObject,
  } = appointment || {};

  const appointmentDate = appointmentDisplayTime || appointment.appointmentDate;
  const doctorName = `${dFirstName} ${dLastName}`;
  const patientFullName = `${pFirstName || ''} ${pLastName || ''}`;
  const userFullName = `${uFirstName || ''} ${uLastName || ''}`;
  const date = moment(appointmentDate).format('DD');
  const month = moment(appointmentDate).format('MMM');
  const time = moment(appointmentDate).format('hh:mm A');
  const paidAmount = get(teleConsultSessionObject, 'pricing_details.total', '-');

  let statusText = 'No Show';
  let statusBgColor = colors.subHeading;

  switch (status) {
    case 'completed':
      statusText = 'Completed';
      statusBgColor = colors.success;
      break;
    case 'cancelled':
      statusText = 'Cancelled';
      statusBgColor = colors.error;
      break;
    case 'active':
    case 'booked':
      statusText = 'Active';
      statusBgColor = colors.primaryBlue;
      break;
  }

  function handleClick() {
    if (onCardClick) {
      onCardClick(appointment);
    }
  }

  function renderInfo() {
    switch (type) {
      case 'appointmentDetail':
        return (
          <>
            <DisplayTime $fs={18} color='currentColor !important' title={time} />
            <InfoText $fs={16} my={'5px'} title={`For ${patientFullName || userFullName}`} />
            <InfoText title={officeID} />
          </>
        );

      default:
        return (
          <>
            <FlexBox>
              {
                appointment.isTeleConsult ? <Icons.VideoIcon /> : <ClinicIcon />
              }
              <StatusBox title={statusText} bg={statusBgColor} />
            </FlexBox>
            <InfoText my={'3px'} title={`Dr. ${doctorName}`} />
            <DisplayTime title={time} />
          </>
        );
    }
  }

  const paymentStatus = TELE_CONSULT_STATUS[teleConsultSessionObject?.status];
  return (
    <Card className={`${className} ${onCardClick ? 'clickable' : ''}`} {...props} onClick={handleClick}>
      <FlexBox pb={'14px'} >
        <DateBox bg={statusBgColor} >
          <Heading2 title={date} />
          <Body1 title={month} color={colors.white} />
        </DateBox>

        <BaseBox flex={1} className='ellipsis'>
          {renderInfo()}
        </BaseBox>
      </FlexBox>

      {
        delay && !hideDelay ? (
          <FlexBox pb='10px' alignItems='center'>
            <TimeIcon mr={'11px'} />
            <Body2
              flex={1}
              fontWeight={600}
              title={`Initial booking - ${getTimeFromMinutes(appointmentTime)}, ${delay} mins delay`}
            />
          </FlexBox>
        ) : null
      }

      {
        !hideFooter && paymentStatus ? (
          <Footer pt={'7px'}>
            <InfoText title={TELE_CONSULT_STATUS[teleConsultSessionObject?.status]} />
            <InfoText ml={'auto'} title={`₹${paidAmount}`} />
          </Footer>
        ) : null
      }

    </Card>
  );
};

export default styled(AppointmentCard)``;